import {
	trackEvent,
	trackPageView,
} from "./analytics";

export const ANALYTICS_EVENTS = {
	PAGE_VIEWED: "Page Viewed",
	BOOKING_STARTED: "Booking Started",
	BRANCH_SELECTED: "Branch Selected",
	ROOM_SEARCH_SUBMITTED: "Room Search Submitted",
	ROOM_SEARCH_SUCCEEDED: "Room Search Succeeded",
	ROOM_SEARCH_FAILED: "Room Search Failed",
	ALTERNATIVES_SHOWN: "Alternatives Shown",
	ALTERNATIVE_SELECTED: "Alternative Selected",
	CONTACT_FORM_VIEWED: "Contact Form Viewed",
	BOOKING_SUBMITTED: "Booking Submitted",
	BOOKING_SUCCEEDED: "Booking Succeeded",
	BOOKING_FAILED: "Booking Failed",
	BOOKING_CANCELLED: "Booking Cancelled",
	SEARCH_ORDER_OPENED: "Search Order Opened",
	SEARCH_ORDER_SUBMITTED: "Search Order Submitted",
	SEARCH_ORDER_SUCCEEDED: "Search Order Succeeded",
	SEARCH_ORDER_FAILED: "Search Order Failed",
};

const pad = (value) => String(value).padStart(2, "0");

const toLocalDateParts = (seconds) => {
	if (!seconds && seconds !== 0) {
		return {};
	}

	const date = new Date(Number(seconds) * 1000);
	if (Number.isNaN(date.getTime())) {
		return {};
	}

	return {
		booking_date: `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`,
		booking_hour: `${pad(date.getHours())}:${pad(date.getMinutes())}`,
	};
};

const parseRoomType = (room) => {
	if (!room) {
		return undefined;
	}

	const value = typeof room === "string" ? room : room?.room || room?.room_type || "";
	const normalized = String(value).toLowerCase();

	if (normalized.includes("small")) return "small";
	if (normalized.includes("medium")) return "medium";
	if (normalized.includes("large")) return "large";

	return normalized || undefined;
};

const getRoomType = (formSearch = {}, room = {}) =>
	parseRoomType(formSearch?.room || room?.room || room?.room_type);

export const getDeviceType = () => {
	if (typeof window === "undefined") {
		return "desktop";
	}

	const width = window.innerWidth || document.documentElement.clientWidth || 0;
	if (width < 768) {
		return "mobile";
	}
	if (width < 1224) {
		return "tablet";
	}
	return "desktop";
};

export const getUtmProperties = (search = "") => {
	if (typeof window === "undefined") {
		return {};
	}

	const params = new URLSearchParams(search || window.location.search || "");
	const utmSource = params.get("utm_source");
	const utmMedium = params.get("utm_medium");
	const utmCampaign = params.get("utm_campaign");
	const utmTerm = params.get("utm_term");
	const utmContent = params.get("utm_content");

	const properties = {};
	if (utmSource) properties.source = utmSource;
	if (utmMedium) properties.medium = utmMedium;
	if (utmCampaign) properties.campaign = utmCampaign;
	if (utmTerm) properties.term = utmTerm;
	if (utmContent) properties.content = utmContent;

	return properties;
};

export const getRouteProperties = (location = {}) => {
	if (typeof window === "undefined") {
		return {};
	}

	const pathname = location.pathname || window.location.pathname;
	const search = location.search || window.location.search || "";

	return {
		page_path: pathname,
		page_search: search,
		page_url: `${window.location.origin}${pathname}${search}`,
		page_title: document.title || "",
		referrer: document.referrer || "",
		device_type: getDeviceType(),
		...getUtmProperties(search),
	};
};

export const getBookingProperties = (formSearch = {}, room = {}, branch = {}) => {
	const bookingSeconds = formSearch?.start_time || room?.start_time;
	const bookingParts = toLocalDateParts(bookingSeconds);
	const durationMinutes = Number(
		formSearch?.time_box === "0"
			? formSearch?.time_box_input
			: formSearch?.time_box || room?.time_box || 0,
	);
	const priceOrder = Number(formSearch?.priceOrder ?? room?.priceOrder ?? 0);
	const priceDiscounted = Number(
		formSearch?.priceOrderDiscount ?? room?.priceOrderDiscount ?? 0,
	);
	const branchId = branch?._id || room?._id || formSearch?.branch || room?.branch || "";
	const branchName = branch?.name || room?.name || formSearch?.branch_name || "";

	return {
		branch_id: branchId,
		branch_name: branchName,
		room_type: getRoomType(formSearch, room),
		duration_minutes: Number.isFinite(durationMinutes) ? durationMinutes : undefined,
		price_order: priceOrder,
		price_discounted: priceDiscounted,
		has_discount: priceOrder > 0 && priceDiscounted > 0 && priceDiscounted < priceOrder,
		...bookingParts,
	};
};

const trackWithContext = (eventName, properties = {}) => {
	trackEvent(eventName, {
		...getRouteProperties(),
		...properties,
	});
};

export const trackBookingStarted = (properties = {}) =>
	trackWithContext(ANALYTICS_EVENTS.BOOKING_STARTED, properties);

export const trackBranchSelected = (properties = {}) =>
	trackWithContext(ANALYTICS_EVENTS.BRANCH_SELECTED, properties);

export const trackRoomSearchSubmitted = (properties = {}) =>
	trackWithContext(ANALYTICS_EVENTS.ROOM_SEARCH_SUBMITTED, properties);

export const trackRoomSearchSucceeded = (properties = {}) =>
	trackWithContext(ANALYTICS_EVENTS.ROOM_SEARCH_SUCCEEDED, properties);

export const trackRoomSearchFailed = (properties = {}) =>
	trackWithContext(ANALYTICS_EVENTS.ROOM_SEARCH_FAILED, properties);

export const trackAlternativesShown = (properties = {}) =>
	trackWithContext(ANALYTICS_EVENTS.ALTERNATIVES_SHOWN, properties);

export const trackAlternativeSelected = (properties = {}) =>
	trackWithContext(ANALYTICS_EVENTS.ALTERNATIVE_SELECTED, properties);

export const trackContactFormViewed = (properties = {}) =>
	trackWithContext(ANALYTICS_EVENTS.CONTACT_FORM_VIEWED, properties);

export const trackBookingSubmitted = (properties = {}) =>
	trackWithContext(ANALYTICS_EVENTS.BOOKING_SUBMITTED, properties);

export const trackBookingSucceeded = (properties = {}) =>
	trackWithContext(ANALYTICS_EVENTS.BOOKING_SUCCEEDED, properties);

export const trackBookingFailed = (properties = {}) =>
	trackWithContext(ANALYTICS_EVENTS.BOOKING_FAILED, properties);

export const trackBookingCancelled = (properties = {}) =>
	trackWithContext(ANALYTICS_EVENTS.BOOKING_CANCELLED, properties);

export const trackSearchOrderOpened = (properties = {}) =>
	trackWithContext(ANALYTICS_EVENTS.SEARCH_ORDER_OPENED, properties);

export const trackSearchOrderSubmitted = (properties = {}) =>
	trackWithContext(ANALYTICS_EVENTS.SEARCH_ORDER_SUBMITTED, properties);

export const trackSearchOrderSucceeded = (properties = {}) =>
	trackWithContext(ANALYTICS_EVENTS.SEARCH_ORDER_SUCCEEDED, properties);

export const trackSearchOrderFailed = (properties = {}) =>
	trackWithContext(ANALYTICS_EVENTS.SEARCH_ORDER_FAILED, properties);

export { trackPageView };
